import {businessTimestampFromDate} from 'src/api/business-time'
import {formatJobTime} from 'src/jobs/job-schedule-format'

import type {DispatchTimeSlot, JobTimeSpan, ScheduleDate} from './types'

/** First hour shown on the day view (24h clock). */
export const WORK_DAY_START_HOUR = 6

/** Last hour shown on the day view (24h clock). */
export const WORK_DAY_END_HOUR = 18

export const SLOTS_PER_DAY = WORK_DAY_END_HOUR - WORK_DAY_START_HOUR + 1

/** Current business-time hour as a decimal (6:30 is 6.5). */
export function businessNowHour(now: Date = new Date()): number {
    const local = businessTimestampFromDate(now)
    return local.getHours() + local.getMinutes() / 60
}

export function buildWorkDaySlots(nowHour = businessNowHour()): DispatchTimeSlot[] {
    const slots: DispatchTimeSlot[] = []
    for (let hour = WORK_DAY_START_HOUR; hour <= WORK_DAY_END_HOUR; hour++) {
        slots.push({
            key: `h${hour}`,
            label: formatHourLabel(hour),
            isNow: hour === Math.floor(nowHour),
        })
    }
    return slots
}

export function formatHourLabel(hour: number): string {
    if (hour === 0) return '12a'
    if (hour < 12) return `${hour}a`
    if (hour === 12) return '12p'
    return `${hour - 12}p`
}

export function formatClockTime(hour: number): string {
    const period = hour < 12 ? 'AM' : 'PM'
    const displayHour = hour % 12 === 0 ? 12 : hour % 12
    return `${displayHour}:00 ${period}`
}

/** Start offset in minutes from `startClock`, falling back to the slot hour. */
export function spanStartMinutes(span: JobTimeSpan): number {
    if (span.startClock) {
        const [hours = 0, minutes = 0] = span.startClock.split(':').map(Number)
        return hours * 60 + minutes
    }
    return slotIndexToHour(span.startSlotIndex) * 60
}

export function spanStartLabel(span: JobTimeSpan): string {
    return span.startClock
        ? formatJobTime(span.startClock)
        : formatClockTime(slotIndexToHour(span.startSlotIndex))
}

/** Human-readable range for a job span (canonical per-day indices + optional day label). */
export function formatSpanTimeRange(span: JobTimeSpan, day?: ScheduleDate): string {
    const startHour = WORK_DAY_START_HOUR + span.startSlotIndex
    const endHour = WORK_DAY_START_HOUR + span.endSlotIndex
    let range = `${formatClockTime(startHour)} – ${formatClockTime(endHour)}`
    if (span.continuesFromPriorDay) range = `From ${formatClockTime(startHour)} (started earlier)`
    if (span.continuesIntoNextDay) range += ' · continues tomorrow'
    if (day) {
        range = `${day.weekdayShort}, ${day.monthLabel} ${day.dayOfMonth} · ${range}`
    }
    return range
}

export function slotIndexToHour(slotIndex: number): number {
    return WORK_DAY_START_HOUR + (slotIndex % SLOTS_PER_DAY)
}
